if (!customElements.get('leavers-form')) {
  customElements.define(
    'leavers-form',
    class LeaversForm extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId || '';
        this.minimumOrder = Number(this.dataset.minimumOrder || '10');
        this.maxRows = Number(this.dataset.maxRows || '8');
        this.rowsWrapper = this.querySelector('[data-order-rows]');
        this.rowTemplate = this.querySelector('[data-order-row-template]');
        this.addRowButton = this.querySelector('[data-add-row]');
        this.totalNode = this.querySelector('[data-order-total]');
        this.statusNode = this.querySelector('[data-order-status]');
        this.summaryInput = this.querySelector('[data-order-summary-input]');
        this.totalInput = this.querySelector('[data-order-total-input]');
        this.form = this.querySelector('form');
        this.garments = this.getGarments();
        this.nextIndex = this.rowsWrapper
          ? this.rowsWrapper.querySelectorAll('[data-order-row]').length + 1
          : 1;

        this.onClick = this.onClick.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
        this.addEventListener('input', this.onInput);
        this.addEventListener('change', this.onInput);
        this.form?.addEventListener('submit', this.onSubmit);
        this.rowsWrapper
          ?.querySelectorAll('[data-order-row]')
          .forEach((row) => this.applyGarmentOption(row));
        this.updateOrderState();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('input', this.onInput);
        this.removeEventListener('change', this.onInput);
        this.form?.removeEventListener('submit', this.onSubmit);
      }

      onClick(event) {
        const colourOption = event.target.closest('[data-colour-option]');
        if (colourOption) {
          event.preventDefault();
          this.selectColourOption(colourOption);
          return;
        }

        const addTrigger = event.target.closest('[data-add-row]');
        if (addTrigger) {
          event.preventDefault();
          this.addOrderRow();
          return;
        }

        const removeTrigger = event.target.closest('[data-remove-row]');
        if (removeTrigger) {
          event.preventDefault();
          this.removeOrderRow(removeTrigger);
        }
      }

      onInput(event) {
        if (event.target.matches('[data-role="garment-type"]')) {
          this.applyGarmentOption(event.target.closest('[data-order-row]'));
          return;
        }

        if (event.target.closest('[data-order-row]')) {
          this.updateOrderState();
        }
      }

      getGarments() {
        const garments = {};

        this.querySelectorAll('[data-garment-config]').forEach((node) => {
          const id = node.dataset.garmentId || '';
          if (!id) return;

          garments[id] = {
            title: node.dataset.garmentTitle || '',
            childPrice: node.dataset.garmentChildPrice || '',
            adultPrice: node.dataset.garmentAdultPrice || '',
            colours: this.parseList(node.dataset.garmentColours),
            childSizes: this.parseList(node.dataset.garmentChildSizes),
            adultSizes: this.parseList(node.dataset.garmentAdultSizes),
            drawstringNote: node.dataset.garmentDrawstringNote || '',
          };
        });

        return garments;
      }

      parseList(value) {
        return (value || '')
          .split(/<br\s*\/?>/i)
          .map((item) => item.trim())
          .filter(Boolean);
      }

      escapeHtml(value) {
        return String(value)
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;')
          .replaceAll("'", '&#39;');
      }

      slugifyColour(value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .replaceAll('&', 'and')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      selectColourOption(option) {
        const colourField = option.closest('[data-colour-field]');
        const hiddenInput = colourField?.querySelector('[data-role="garment-colour"]');

        if (!colourField || !hiddenInput) return;

        colourField.querySelectorAll('[data-colour-option]').forEach((button) => {
          const isSelected = button === option;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        hiddenInput.value = option.dataset.value || '';
        this.updateOrderState();
      }

      applyGarmentOption(row) {
        if (!row) return;

        const garmentSelect = row.querySelector('[data-role="garment-type"]');
        const sizeSelect = row.querySelector('[data-role="garment-size"]');
        const colourField = row.querySelector('[data-colour-field]');
        const colourOptions = colourField?.querySelector('[data-colour-options]');
        const colourInput = colourField?.querySelector('[data-role="garment-colour"]');
        const metaNode = row.querySelector('[data-row-garment-meta]');
        const garment = this.garments[garmentSelect?.value || ''];

        if (sizeSelect) {
          sizeSelect.innerHTML = '<option value="">Select size</option>';

          if (garment?.childSizes?.length) {
            const childGroup = document.createElement('optgroup');
            childGroup.label = 'Children';
            garment.childSizes.forEach((size) => {
              childGroup.append(new Option(size, size));
            });
            sizeSelect.append(childGroup);
          }

          if (garment?.adultSizes?.length) {
            const adultGroup = document.createElement('optgroup');
            adultGroup.label = 'Adults';
            garment.adultSizes.forEach((size) => {
              adultGroup.append(new Option(size, size));
            });
            sizeSelect.append(adultGroup);
          }
        }

        if (colourInput) {
          colourInput.value = '';
        }

        if (colourOptions) {
          if (garment?.colours?.length) {
            colourOptions.innerHTML = garment.colours
              .map((colour) => {
                const escaped = this.escapeHtml(colour);
                const swatchName = this.escapeHtml(this.slugifyColour(colour));
                return `<button class="st-leavers-form__swatch-button" type="button" data-colour-option data-value="${escaped}" data-swatch-name="${swatchName}" aria-pressed="false"><span class="st-leavers-form__swatch-dot" aria-hidden="true"></span><span>${escaped}</span></button>`;
              })
              .join('');
          } else {
            colourOptions.innerHTML = '';
          }
        }

        if (metaNode) {
          if (garment) {
            const priceParts = [];
            if (garment.childPrice) priceParts.push(`Children ${garment.childPrice}`);
            if (garment.adultPrice) priceParts.push(`Adults ${garment.adultPrice}`);

            let metaText = priceParts.join(' | ');
            if (garment.drawstringNote) {
              metaText = metaText ? `${metaText}. ${garment.drawstringNote}` : garment.drawstringNote;
            }

            metaNode.textContent = metaText || 'Select colours and size for this garment.';
          } else {
            metaNode.textContent = 'Select a garment to load its colours, sizes and pricing.';
          }
        }

        this.updateOrderState();
      }

      onSubmit(event) {
        if (!this.updateOrderState()) {
          event.preventDefault();
          this.statusNode?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

      addOrderRow() {
        if (!this.rowsWrapper || !this.rowTemplate) return;

        const existingRows = this.rowsWrapper.querySelectorAll('[data-order-row]').length;
        if (existingRows >= this.maxRows) {
          return;
        }

        const html = this.rowTemplate.innerHTML.replace(/__INDEX__/g, String(this.nextIndex));
        this.rowsWrapper.insertAdjacentHTML('beforeend', html);
        const newRow = this.rowsWrapper.lastElementChild;
        this.nextIndex += 1;
        this.applyGarmentOption(newRow);
      }

      removeOrderRow(trigger) {
        const row = trigger.closest('[data-order-row]');
        if (!row || !this.rowsWrapper) return;

        row.remove();

        if (!this.rowsWrapper.querySelector('[data-order-row]')) {
          this.addOrderRow();
          return;
        }

        this.updateOrderState();
      }

      buildOrderSummary() {
        if (!this.rowsWrapper) return { total: 0, summary: '' };

        const rows = Array.from(this.rowsWrapper.querySelectorAll('[data-order-row]'));
        let total = 0;
        const lines = [];

        rows.forEach((row, index) => {
          const garmentId = row.querySelector('[data-role="garment-type"]')?.value?.trim() || '';
          const garment = this.garments[garmentId]?.title || '';
          const colour = row.querySelector('[data-role="garment-colour"]')?.value?.trim() || '';
          const size = row.querySelector('[data-role="garment-size"]')?.value?.trim() || '';
          const quantityValue = row.querySelector('[data-role="garment-quantity"]')?.value || '';
          const quantity = Number(quantityValue || '0');

          const rowLabelNode = row.querySelector('[data-row-label]');
          if (rowLabelNode) {
            rowLabelNode.textContent = `Item ${index + 1}`;
          }

          if (quantity > 0) {
            total += quantity;
          }

          if (garment || colour || size || quantity > 0) {
            lines.push(
              `Item ${index + 1}: ${garment || 'No garment selected'} | ${colour || 'No colour selected'} | ${size || 'No size selected'} | Qty ${quantity > 0 ? quantity : 0}`
            );
          }
        });

        return {
          total,
          summary: lines.join('\n'),
        };
      }

      updateOrderState() {
        const rows = this.rowsWrapper
          ? Array.from(this.rowsWrapper.querySelectorAll('[data-order-row]'))
          : [];
        const { total, summary } = this.buildOrderSummary();
        const meetsMinimum = total >= this.minimumOrder;

        if (this.totalNode) {
          this.totalNode.textContent = String(total);
        }

        if (this.totalInput) {
          this.totalInput.value = String(total);
        }

        if (this.summaryInput) {
          this.summaryInput.value = summary || 'No garment rows added.';
        }

        if (this.statusNode) {
          this.statusNode.classList.remove('is-warning', 'is-success');
          if (meetsMinimum) {
            this.statusNode.classList.add('is-success');
            this.statusNode.textContent = `Minimum met. ${total} garments selected.`;
          } else {
            const remaining = Math.max(this.minimumOrder - total, 0);
            this.statusNode.classList.add('is-warning');
            this.statusNode.textContent = `Add ${remaining} more garment${remaining === 1 ? '' : 's'} to reach the ${this.minimumOrder}-garment minimum order.`;
          }
        }

        rows.forEach((row) => {
          const removeButton = row.querySelector('[data-remove-row]');
          if (removeButton) {
            removeButton.disabled = rows.length === 1;
          }
        });

        if (this.addRowButton) {
          const atMax = rows.length >= this.maxRows;
          this.addRowButton.disabled = atMax;
          this.addRowButton.setAttribute('aria-disabled', atMax ? 'true' : 'false');
        }

        return meetsMinimum;
      }
    }
  );
}
